import { useMemo, useState } from "react";
import { applyCanvasReferenceCommandV3, getPromptContent, type CanvasDocumentV3 as CanvasDocument, type PromptContent, type PromptSource } from "@seedance/contracts";
import { referenceSnapshotForDocument, type EditorReferenceSnapshot } from "../reference-snapshot.js";
import { StructuredPromptEditor, type StructuredPromptEditorProps } from "../prompts/StructuredPromptEditor.js";
import { PromptBindingStatus } from "../prompts/PromptBindingStatus.js";
import { openPromptReferenceSelection, promptReferenceContexts, selectPromptReference, type PromptReferenceSelection } from "../editing-dispatch.js";

export interface InlineTextEditorProps {
  value: PromptContent; onChange(value: PromptContent): void;
  onUndo?(): void; onRedo?(): void;
}
export function InlineTextEditor(props: InlineTextEditorProps) {
  return <StructuredPromptEditor {...props} ariaLabel="直接编辑文本" maxLength={50_000} onRequestReference={() => undefined} />;
}

/** Shared sources have no global binding: choose a consuming target explicitly. */
export function CanvasPromptEditor({document, source, targetNodeId, referenceSnapshot, onChange, ariaLabel, maxLength, onUndo, onRedo}: {
  document: CanvasDocument; source: PromptSource; targetNodeId?: string;
  referenceSnapshot?: EditorReferenceSnapshot;
  onChange(document: CanvasDocument): void; ariaLabel: string; maxLength: number;
  onUndo?: StructuredPromptEditorProps["onUndo"]; onRedo?: StructuredPromptEditorProps["onRedo"];
}) {
  const content = getPromptContent(document, source);
  const [selection, setSelection] = useState<PromptReferenceSelection>();
  const [error, setError] = useState<string>();
  const contexts = targetNodeId ? promptReferenceContexts(document,source,targetNodeId) : [];
  const contextKeys = new Set(contexts.map(offer => JSON.stringify(offer.source)));
  const scoped = contextKeys.size === 1 ? contexts[0]?.source : undefined;
  const select = (offerIndex:number) => {
    if (!selection) return;
    try {
      if (JSON.stringify(source) !== JSON.stringify(selection.source) || (targetNodeId && selection.offers[offerIndex]?.targetNodeId !== targetNodeId)) throw new Error("当前编辑节点已切换，请重新选择引用。");
      onChange(selectPromptReference(document,selection,offerIndex));
      setSelection(undefined); setError(undefined);
    } catch (error) { setError(error instanceof Error ? error.message : "引用选择失败，原内容已保留。"); }
  };
  const target = document.nodes.find(node => node.id === targetNodeId && node.kind === "seedance");
  const references = useMemo(() => targetNodeId ? referenceSnapshotForDocument(document,referenceSnapshot).resolve(targetNodeId).references : [], [document,targetNodeId,referenceSnapshot]);
  return <>
    <StructuredPromptEditor value={content} ariaLabel={ariaLabel} maxLength={maxLength}
      {...(onUndo ? {onUndo} : {})} {...(onRedo ? {onRedo} : {})}
      onChange={content => onChange(applyCanvasReferenceCommandV3(document,{type:"set_prompt",source,content}))}
      onRequestReference={range => setSelection(openPromptReferenceSelection(document,source,range,targetNodeId))}
      renderReference={(reference,actions) => {
        const binding = target?.kind === "seedance" && scoped ? target.referenceBindings.find(binding => binding.slotId === reference.slotId && binding.source.nodeId === scoped.nodeId && binding.source.field === scoped.field && binding.source.inputEdgeId === scoped.inputEdgeId) : undefined;
        const bound = binding?.value.state === "bound" ? binding.value.target : undefined;
        const targetLabel = bound ? references.find(item => item.edgeId === bound.edgeId && item.nodeId === bound.nodeId && item.assetId === bound.assetId && item.kind === bound.kind)?.mention : undefined;
        return <PromptBindingStatus reference={reference} {...(binding ? {binding} : {})} {...(targetLabel ? {targetLabel} : {})} hasTargetContext={Boolean(scoped)} onRebind={actions.onRebind} onRemove={actions.onRemove} />;
      }} />
    {selection ? <div role="dialog" aria-label="绑定提示词引用">
      {selection.offers.map((offer,index) => <button key={offer.targetNodeId+":"+offer.source.inputEdgeId+":"+offer.target.edgeId} type="button" onClick={() => select(index)}>{offer.contextLabel} · {offer.mention}</button>)}
      {!selection.offers.length ? <p>请先将此提示词连接到生成节点，并连接可用的参考素材。</p> : null}
      <button type="button" onClick={() => setSelection(undefined)}>取消引用选择</button>
    </div> : null}
    {error ? <p role="alert">{error}</p> : null}
  </>;
}
